import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'alloc-warrior-codex';

const EMPTY = { visitedNodes: [], endingsUnlocked: [], charactersPlayed: [] };

// ---------- localStorage helpers ----------

function readLocal() {
  if (typeof window === 'undefined') return { ...EMPTY };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    const data = JSON.parse(raw);
    return {
      visitedNodes: Array.isArray(data.visitedNodes) ? data.visitedNodes : [],
      endingsUnlocked: Array.isArray(data.endingsUnlocked) ? data.endingsUnlocked : [],
      charactersPlayed: Array.isArray(data.charactersPlayed) ? data.charactersPlayed : [],
    };
  } catch {
    return { ...EMPTY };
  }
}

function writeLocal(codex) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(codex));
  } catch {
    // ignore quota errors
  }
}

// ---------- merge helpers ----------

function mergeArrays(...arrs) {
  return Array.from(new Set(arrs.flat().filter(Boolean)));
}

function mergeCodex(a, b) {
  return {
    visitedNodes: mergeArrays(a.visitedNodes, b.visitedNodes),
    endingsUnlocked: mergeArrays(a.endingsUnlocked, b.endingsUnlocked),
    charactersPlayed: mergeArrays(a.charactersPlayed, b.charactersPlayed),
  };
}

function isSameCodex(a, b) {
  if (a.visitedNodes.length !== b.visitedNodes.length) return false;
  if (a.endingsUnlocked.length !== b.endingsUnlocked.length) return false;
  if (a.charactersPlayed.length !== b.charactersPlayed.length) return false;
  const setEq = (x, y) => x.every((v) => y.includes(v));
  return (
    setEq(a.visitedNodes, b.visitedNodes) &&
    setEq(a.endingsUnlocked, b.endingsUnlocked) &&
    setEq(a.charactersPlayed, b.charactersPlayed)
  );
}

// ---------- serveur ----------

async function lireCodexServeur() {
  try {
    const res = await fetch('/api/codex');
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[useCodex] lecture serveur impossible :', err.message);
    return null;
  }
}

async function ecrireCodexServeur(codex) {
  try {
    const res = await fetch('/api/codex', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(codex),
    });
    return res.ok;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[useCodex] écriture serveur impossible :', err.message);
    return false;
  }
}

// ---------- hook ----------

/**
 * Persistent meta-progress: which story nodes were discovered,
 * which endings unlocked, which characters played.
 *
 * - Reads from localStorage immediately for instant UI.
 * - Once the session is known, fetches the server row, merges it with the local
 *   state, then writes the merged result back. From then on, every local mutation
 *   is debounced-pushed to the server.
 */
export function useCodex(user) {
  const [codex, setCodex] = useState(readLocal);
  const [syncStatus, setSyncStatus] = useState('local'); // 'local' | 'syncing' | 'synced' | 'error'

  const userIdRef = useRef(null);
  const initialMergeDoneRef = useRef(false);
  const pushTimerRef = useRef(null);

  // -- Initial load + merge from the server once we know who the user is --
  useEffect(() => {
    if (!user?.id) return;
    if (userIdRef.current === user.id && initialMergeDoneRef.current) return;

    userIdRef.current = user.id;
    let cancelled = false;

    (async () => {
      setSyncStatus('syncing');
      const distant = await lireCodexServeur();
      if (cancelled) return;

      const local = readLocal();
      const merged = distant ? mergeCodex(local, distant) : local;

      writeLocal(merged);
      setCodex(merged);

      if (!distant || !isSameCodex(merged, distant)) {
        const ok = await ecrireCodexServeur(merged);
        if (cancelled) return;
        setSyncStatus(ok ? 'synced' : 'error');
      } else {
        setSyncStatus('synced');
      }
      initialMergeDoneRef.current = true;
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // -- Debounced push on every change (after initial merge) --
  const schedulePush = useCallback((next) => {
    if (!userIdRef.current || !initialMergeDoneRef.current) return;
    if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(async () => {
      setSyncStatus('syncing');
      const ok = await ecrireCodexServeur(next);
      setSyncStatus(ok ? 'synced' : 'error');
    }, 500);
  }, []);

  // -- Mutations --

  const recordVisit = useCallback(
    (nodeId) => {
      if (!nodeId) return;
      setCodex((prev) => {
        if (prev.visitedNodes.includes(nodeId)) return prev;
        const next = { ...prev, visitedNodes: [...prev.visitedNodes, nodeId] };
        writeLocal(next);
        schedulePush(next);
        return next;
      });
    },
    [schedulePush]
  );

  const recordEnding = useCallback(
    (endingId) => {
      if (!endingId) return;
      setCodex((prev) => {
        if (prev.endingsUnlocked.includes(endingId)) return prev;
        const next = { ...prev, endingsUnlocked: [...prev.endingsUnlocked, endingId] };
        writeLocal(next);
        schedulePush(next);
        return next;
      });
    },
    [schedulePush]
  );

  const recordCharacter = useCallback(
    (charId) => {
      if (!charId) return;
      setCodex((prev) => {
        if (prev.charactersPlayed.includes(charId)) return prev;
        const next = { ...prev, charactersPlayed: [...prev.charactersPlayed, charId] };
        writeLocal(next);
        schedulePush(next);
        return next;
      });
    },
    [schedulePush]
  );

  return { codex, syncStatus, recordVisit, recordEnding, recordCharacter };
}
